import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";

import beverageImageSrc0 from "../../assets/beverageV7/0a.jpg";
import beverageImageSrc1 from "../../assets/beverageV6/1.jpg";
import beverageImageSrc2 from "../../assets/beverageV6/2.jpg";
import beverageImageSrc3 from "../../assets/beverageV6/3.jpg";
import beverageImageSrc4 from "../../assets/beverageV6/4.jpg";
import beverageImageSrc5 from "../../assets/beverageV6/5.jpg";
import { BeveragesGallery } from "./BeveragesGallery";
import { BeveragesDialog } from "./BeveragesDialog";
import { BeveragesDetailsDialog } from "./BeveragesDetailsDialog";
import actionWebInvoke from "../../utils/actionWebInvoke";
import actions from "../../config.json";
import { useAcid } from "../../hooks/useAcid";
import { SignInDialogUi, ConsentPopup } from "../../components";
import { BeveragesSuccessDialog } from "./BeveragesSuccessDialog";
import { isBeverageDataValid } from "./utils";
import {
  sendAnalyticsPageViewEvent,
  sendPageViewWhenConsentReady,
} from "../../utils/adobeDataLayerUtils";
import { buildStructuredBeverageDataFromCategoryDetails } from "../../utils/beveragePayload";
import {
  getStoredBeverageConsent,
  shouldResetBeverageStateForAcidChange,
} from "../../utils/beverageSession";
import { stripAcidFromSearch } from "../../utils/postLoginRedirect";
import {
  buildPendingBeverageV7Selections,
  getBeverageV7PostPreferenceSaveState,
} from "./signInReturnFlow";

import styles from "./Beverages.module.css";

const images = [
  beverageImageSrc0,
  beverageImageSrc1,
  beverageImageSrc2,
  beverageImageSrc3,
  beverageImageSrc4,
  beverageImageSrc5,
];

export const BeverageV7 = () => {
  const cid = "beverageV7";
  const location = useLocation();
  const [isCategoriesDialogShown, setIsCategoriesDialogShown] = useState(false);
  const [isCustomizeDialogShown, setIsCustomizeDialogShown] = useState(false);
  const [isSignInDialogShown, setIsSignInDialogShown] = useState(false);
  const [isSuccessDialogShown, setIsSuccessDialogShown] = useState(false);
  const [isConsentDialogShown, setIsConsentDialogShown] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesDetails, setCategoriesDetails] = useState({});
  const [arePreferencesSaved, setArePreferencesSaved] = useState(false);
  const [pendingPreferenceSave, setPendingPreferenceSave] = useState(null);
  const { acid } = useAcid();
  const [isSaving, setIsSaving] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const submitLockRef = useRef(false);
  const pendingDetailsForSignInRef = useRef(null);
  const pendingSignInIntentRef = useRef(null);
  const previousAcidRef = useRef(acid);
  const currentAcidRef = useRef(acid);

  const resetBeverageStateForAcidChange = useCallback(() => {
    setIsCategoriesDialogShown(false);
    setIsCustomizeDialogShown(false);
    setIsSignInDialogShown(false);
    setIsSuccessDialogShown(false);
    setIsConsentDialogShown(false);
    setCategories([]);
    setCategoriesDetails({});
    setArePreferencesSaved(false);
    setPendingPreferenceSave(null);
    setIsSaving(false);
    setIsRedirecting(false);
    submitLockRef.current = false;
    pendingDetailsForSignInRef.current = null;
    pendingSignInIntentRef.current = null;
  }, []);

  useEffect(() => {
    currentAcidRef.current = acid;
    if (shouldResetBeverageStateForAcidChange(previousAcidRef.current, acid)) {
      resetBeverageStateForAcidChange();
    }
    previousAcidRef.current = acid;
  }, [acid, resetBeverageStateForAcidChange]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImageIndex((prev) => {
        const next = prev + 1;
        if (next >= images.length) {
          return 0;
        }
        return next;
      });
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    return sendPageViewWhenConsentReady(
      "Beverage V7 Page",
      "Passions-FavBevs",
      "beverages7",
    );
  }, []);

  // Send analytics event when categories dialog opens
  useEffect(() => {
    if (isCategoriesDialogShown) {
      sendAnalyticsPageViewEvent(
        "Beverage V7 Modal - Categories",
        "Passions-FavBevs",
        "beverages7",
      );
    }
  }, [isCategoriesDialogShown]);

  // Send analytics event when customize dialog opens
  useEffect(() => {
    if (isCustomizeDialogShown) {
      sendAnalyticsPageViewEvent(
        "Beverage V7 Modal - Customize",
        "Passions-FavBevs",
        "beverages7",
      );
    }
  }, [isCustomizeDialogShown]);

  useEffect(() => {
    if (isSignInDialogShown) {
      sendAnalyticsPageViewEvent(
        "Beverage V7 Page Modal: Sign In",
        "Passions-FavBevs",
        "beverages7",
      );
    }
  }, [isSignInDialogShown]);

  useEffect(() => {
    if (isConsentDialogShown) {
      sendAnalyticsPageViewEvent(
        "Beverage V7 Page Modal: Consent",
        "Passions-FavBevs",
        "beverages7",
      );
    }
  }, [isConsentDialogShown]);

  useEffect(() => {
    if (isSuccessDialogShown) {
      sendAnalyticsPageViewEvent(
        "Beverage V7 Page Modal: Success",
        "Passions-FavBevs",
        "beverages7",
      );
    }
  }, [isSuccessDialogShown]);

  const onSubmitLoginDialog = () => {
    try {
      sessionStorage.setItem(
        "pendingBeverageV7Selections",
        JSON.stringify(
          buildPendingBeverageV7Selections({
            categories,
            categoriesDetails,
            pendingDetails: pendingDetailsForSignInRef.current,
            continueToPersonalize:
              pendingSignInIntentRef.current === "personalize",
          }),
        ),
      );
    } catch (error) {
      console.error("❌ Failed to save beverage selections:", error);
    }
  };

  const isValid = useMemo(() => {
    return isBeverageDataValid(categoriesDetails);
  }, [categoriesDetails]);

  const hasAnsweredConsent = useCallback(() => {
    return getStoredBeverageConsent().hasResponded;
  }, []);

  const saveWithDetails = useCallback(
    async (freshDetails, categoryList = categories) => {
      if (!acid) {
        pendingDetailsForSignInRef.current = freshDetails;
        pendingSignInIntentRef.current = "save";
        setCategoriesDetails(freshDetails);
        setArePreferencesSaved(false);
        setIsSignInDialogShown(true);
        return false;
      }

      if (submitLockRef.current) return false;
      submitLockRef.current = true;

      try {
        const requestAcid = acid;
        setIsSaving(true);

        const data = buildStructuredBeverageDataFromCategoryDetails(
          categoryList,
          freshDetails,
        );

        await actionWebInvoke(
          actions["marriott-passions-v3/save-beverage-preferences"],
          {},
          { acid: requestAcid, structuredBeverageData: data },
        );
        if (currentAcidRef.current !== requestAcid) {
          return false;
        }
        setArePreferencesSaved(true);
        setPendingPreferenceSave(null);
        pendingDetailsForSignInRef.current = null;
        return true;
      } catch (err) {
        console.error("Error saving beverage selection:", err);
        setArePreferencesSaved(false);
        return false;
      } finally {
        setIsSaving(false);
        submitLockRef.current = false;
      }
    },
    [acid, categories],
  );

  const saveConsent = useCallback(
    async (optedOut) => {
      if (!acid) {
        setIsConsentDialogShown(false);
        setIsSignInDialogShown(true);
        return;
      }

      if (submitLockRef.current) return;
      submitLockRef.current = true;

      setIsSaving(true);
      try {
        const requestAcid = acid;
        await actionWebInvoke(
          actions["marriott-passions-v3/save-beverage-preferences"],
          {},
          { acid: requestAcid, optedOutPersonalization: optedOut },
        );
        if (currentAcidRef.current !== requestAcid) {
          return;
        }
        sessionStorage.setItem("beverageConsentResponded", "true");
        sessionStorage.setItem("beverageConsentOptedOut", String(optedOut));
        setIsConsentDialogShown(false);
        setIsSuccessDialogShown(true);
      } catch (err) {
        console.error("Error saving consent:", err);
      } finally {
        setIsSaving(false);
        submitLockRef.current = false;
      }
    },
    [acid],
  );

  const agree = useCallback(async () => {
    await saveConsent(false);
  }, [saveConsent]);

  const disagree = useCallback(async () => {
    await saveConsent(true);
  }, [saveConsent]);

  const personalizeMyStay = useCallback(async () => {
    if (!acid) {
      pendingDetailsForSignInRef.current = categoriesDetails;
      pendingSignInIntentRef.current = "personalize";
      setIsSignInDialogShown(true);
      return;
    }

    if (!arePreferencesSaved) {
      const saved = await saveWithDetails(categoriesDetails);
      if (!saved) {
        return;
      }
    }

    // Show consent popup when user clicks "Personalize My Stay"
    if (!hasAnsweredConsent()) {
      setIsConsentDialogShown(true);
      return;
    }

    setIsSuccessDialogShown(true);
  }, [
    acid,
    arePreferencesSaved,
    categoriesDetails,
    hasAnsweredConsent,
    saveWithDetails,
  ]);

  useEffect(() => {
    setCategoriesDetails((prev) => {
      const newDetails = {};
      categories.forEach((category) => {
        newDetails[category] = prev[category] ?? {
          timeOfDay: [],
          additionalDetails: "",
          types: [],
        };
      });
      return newDetails;
    });
  }, [categories]);

  useEffect(() => {
    try {
      const strData = sessionStorage.getItem("pendingBeverageV7Selections");
      const parsed = JSON.parse(strData);
      const restoredCategories = parsed?.categories ?? [];
      const restoredDetails = parsed?.categoriesDetails ?? {};
      setCategories(restoredCategories);
      setCategoriesDetails(restoredDetails);
      setArePreferencesSaved(false);
      if (
        parsed?.shouldAutoSavePreferences &&
        restoredCategories.length > 0 &&
        isBeverageDataValid(restoredDetails)
      ) {
        setPendingPreferenceSave({
          categories: restoredCategories,
          categoriesDetails: restoredDetails,
          continueToPersonalize: parsed?.continueToPersonalize === true,
        });
      }
      sessionStorage.removeItem("pendingBeverageV7Selections");
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (!acid || !pendingPreferenceSave) {
      return;
    }

    let isMounted = true;

    const saveRestoredPreferences = async () => {
      const { continueToPersonalize } = pendingPreferenceSave;
      const saved = await saveWithDetails(
        pendingPreferenceSave.categoriesDetails,
        pendingPreferenceSave.categories,
      );
      if (saved && isMounted) {
        setPendingPreferenceSave(null);
        if (continueToPersonalize) {
          const nextState = getBeverageV7PostPreferenceSaveState({
            continueToPersonalize,
            hasAnsweredConsent: hasAnsweredConsent(),
          });
          if (nextState === "consent") {
            setIsConsentDialogShown(true);
          } else if (nextState === "success") {
            setIsSuccessDialogShown(true);
          }
        }
      }
    };

    saveRestoredPreferences();

    return () => {
      isMounted = false;
    };
  }, [acid, pendingPreferenceSave, saveWithDetails, hasAnsweredConsent]);

  const onSuccessDialogClose = () => {
    setIsRedirecting(true);
    setTimeout(() => {
      window.location.href = "https://www.marriott.com/default.mi";
    }, 1000);
  };

  return (
    <main className={styles.main}>
      <div className={styles.banner}>
        <div className={styles.bannerImage}>
          {images.map((image, index) => (
            <img
              src={image}
              key={index}
              className={activeImageIndex === index ? styles.active : null}
            />
          ))}
          <div className={styles.overlay}></div>
        </div>
        <div className={styles.bannerText}>
          <h1>
            What beverages do you prefer?
            <br />
            Help us get to know you better.
          </h1>

          <p>
            From morning coffee to evening cocktails, tell us what completes
            your day.
          </p>

          <button
            onClick={() => setIsCategoriesDialogShown(true)}
            className={`${styles.getStartedButton} custom_click_track`}
            data-custom_click_track_value="Beverages|Get Started|internal"
          >
            Get Started
          </button>
        </div>
      </div>

      <div className={styles.galleryWrapper}>
        <h2 className={styles.selectionsTitle}>Your selections</h2>
        <BeveragesGallery
          categories={categories}
          onAddClick={() => setIsCategoriesDialogShown(true)}
          hideAddButton={categories.length === 0}
        />

        {categories.length > 0 && (
          <>
            <div className={styles.buttonWrapper}>
              <button
                disabled={!categories.length || !isValid || isSaving}
                onClick={personalizeMyStay}
                className={`${styles.primary} custom_click_track`}
                data-custom_click_track_value="Beverages|Personalize My Stay|internal"
              >
                Personalize My Stay
              </button>
            </div>

            <p className={styles.privacyText}>
              Preferences can be updated at any time in my{" "}
              <a
                href="https://www.marriott.com/loyalty/myAccount/default.mi"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.privacyLink}
              >
                Bonvoy Account Profile
              </a>
              , and I can learn more about how my data is collected and used in
              the{" "}
              <a
                href="https://www.marriott.com/about/privacy.mi"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.privacyLink}
              >
                Global Privacy Statement
              </a>
              .
            </p>
          </>
        )}
      </div>

      {isCategoriesDialogShown && (
        <BeveragesDialog
          initiallySelectedCategories={categories}
          setCategories={(nextCategories) => {
            setCategories(nextCategories);
            setArePreferencesSaved(false);
          }}
          customize={() => setIsCustomizeDialogShown(true)}
          onClose={() => setIsCategoriesDialogShown(false)}
        />
      )}

      {isCustomizeDialogShown && (
        <BeveragesDetailsDialog
          categoriesDetails={categoriesDetails}
          setNewDetails={(nextDetails) => {
            setCategoriesDetails(nextDetails);
            setArePreferencesSaved(false);
          }}
          onClose={() => setIsCustomizeDialogShown(false)}
          onBack={() => {
            setIsCustomizeDialogShown(false);
            setIsCategoriesDialogShown(true);
          }}
          isSaving={isSaving}
          onSave={async (details) => {
            const saved = await saveWithDetails(details);
            if (saved) {
              pendingDetailsForSignInRef.current = null;
            }
            if (saved || !acid) {
              setIsCustomizeDialogShown(false);
            }
          }}
        />
      )}

      {isSignInDialogShown && (
        <SignInDialogUi
          onClose={() => setIsSignInDialogShown(false)}
          onSubmit={onSubmitLoginDialog}
          selectedPassions={[]}
          cid={cid}
          redirectUrl={`/beverage7${stripAcidFromSearch(location.search)}`}
        />
      )}

      {isSuccessDialogShown && (
        <BeveragesSuccessDialog
          isRedirecting={isRedirecting}
          onClose={onSuccessDialogClose}
        ></BeveragesSuccessDialog>
      )}

      {isConsentDialogShown && (
        <ConsentPopup
          onAccept={agree}
          onDecline={disagree}
          onClose={() => setIsConsentDialogShown(false)}
          loading={isSaving}
        />
      )}
    </main>
  );
};
